import TimelineDragIcon from "@/assets/svg/timeline-drag.svg";
import { Container } from "@/components/Container";
import classNames from "classnames/bind";
import { Draggable } from "gsap/Draggable";
import { forwardRef, Ref, RefObject, useEffect, useRef } from "react";

import { useInducteeLanding } from "@/providers/InducteeLandingContext";
import { useViewSize } from "@/providers/ViewSizeContext";
import gsap from "gsap";
import { useLenis } from "lenis/react";
import styles from "./Timeline.module.scss";
const cx = classNames.bind(styles);

export interface TimelineProps {
  startYear: number;
  endYear: number;
  className?: string;
  dragProgress?: number;
  onDragStart: () => void;
  onTimelineDrag: (self: Draggable) => void;
  onTimelineMarkClick: (index: number) => void;
  dragRef: RefObject<HTMLButtonElement>;
}

interface TickMark {
  year: number;
  position: number; // percentage position (0-100)
  type: "large" | "medium" | "small";
  showLabel?: boolean;
}

const generateTickMarks = (startYear: number, endYear: number): TickMark[] => {
  const tickMarks: TickMark[] = [];
  const totalYears = startYear - endYear;

  // Helper function to calculate position percentage
  const getPosition = (year: number): number => {
    return ((startYear - year) / totalYears) * 100;
  };

  // Add all years to determine tick types
  for (let year = endYear; year <= startYear; year++) {
    const position = getPosition(year);
    let type: "large" | "medium" | "small" = "small";
    let showLabel = false;

    if (year === startYear || year === endYear || year % 10 === 0) {
      type = "large";
      showLabel = true;
    } else {
      const yearsSinceLastLarge = year % 10;
      if (
        yearsSinceLastLarge === 2 ||
        yearsSinceLastLarge === 4 ||
        yearsSinceLastLarge === 6 ||
        yearsSinceLastLarge === 8
      ) {
        type = "medium";
      } else {
        type = "small";
      }
    }

    tickMarks.push({
      year,
      position,
      type,
      showLabel,
    });
  }

  return tickMarks;
};

const Timeline = forwardRef<HTMLDivElement, TimelineProps>(
  (
    {
      startYear,
      endYear,
      className = "",
      onDragStart,
      onTimelineDrag,
      onTimelineMarkClick,
      dragProgress,
      dragRef,
    },
    ref
  ) => {
    const trackEl = useRef<HTMLDivElement>(null);
    const dragEl = useRef<HTMLButtonElement>(null);
    const tickMarks = generateTickMarks(startYear, endYear);
    const draggable = useRef<Draggable[]>([]);
    const { viewWidth } = useViewSize();
    const { scrollCoef, setScrollCoef } = useInducteeLanding();
    const lenis = useLenis();

    useEffect(() => {
      dragRef.current = dragEl.current;
    }, [dragEl]);

    useEffect(() => {
      if (draggable.current.length === 0 || draggable.current[0].isDragging) {
        return;
      }
      const draggableRef = draggable.current[0];
      const boundsRect = draggableRef.vars.bounds as Draggable.BoundsMinMax;

      gsap.set(dragEl.current, {
        x: (boundsRect.maxX - boundsRect.minX) * scrollCoef,
      });
      // console.log(footerHeight, viewWidth);
      draggableRef.update(true);
    }, [scrollCoef]);

    useEffect(() => {
      gsap.registerPlugin(Draggable);
      if (trackEl.current && dragEl.current) {
        const snaps = tickMarks.reduce((acc, tick) => {
          const perc = (tick.position / 100) * trackEl.current.offsetWidth;
          acc.push(perc);
          return acc;
        }, []);
        draggable.current = Draggable.create(dragEl.current, {
          type: "x",
          inertia: false,
          onPress: onDragStart,
          onDrag: function () {
            onTimelineDrag(this);
          },
          onThrowUpdate: function () {
            onTimelineDrag(this);
          },
          bounds: {
            minX: 0,
            maxX: trackEl.current.offsetWidth,
          },
        });
      }
    }, [trackEl, onTimelineDrag]);

    useEffect(() => {
      const draggableEl = draggable.current[0];
      const snaps = tickMarks.reduce((acc, tick) => {
        const perc = (tick.position / 100) * trackEl.current.offsetWidth;
        acc.push(perc);
        return acc;
      }, []);
      draggableEl.vars.snap = snaps;
      draggableEl.update(true);
      draggableEl.applyBounds({
        minX: 0,
        maxX: trackEl.current.offsetWidth,
      });
    }, [draggable.current, viewWidth]);

    return (
      <div aria-hidden="true" className={cx(["timeline", className])} ref={ref}>
        <Container className={cx("container")}>
          <div className={cx("track", "offset-element")} ref={trackEl}>
            <button
              className={cx("timeline-drag")}
              type="button"
              ref={dragEl}
              tabIndex={-1}
              aria-label="Timeline slider"
            >
              <TimelineDragIcon />
            </button>
            {tickMarks.map((tick, index) => (
              <div
                key={tick.year}
                className={cx(["mark", `mark--${tick.type}`])}
                onClick={() => onTimelineMarkClick(index)}
                style={{ left: `${tick.position}%` }}
              >
                <div className={cx("line")} />
                {tick.showLabel && (
                  <div className={cx("label", "eyebrow-l")}>{tick.year}</div>
                )}
              </div>
            ))}
          </div>
        </Container>
      </div>
    );
  }
);

export default Timeline;
